'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { jwtDecode } from 'jwt-decode'
import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { loginGoogleAction } from '@/actions/auth/login-google-action'
import { trackEvent } from '@/components/@analitycs/trackEvent'
import { Skeleton } from '@/components/ui/skeleton'
import { useGTM } from '@/hooks/use-gtm'

export default function AuthGoogleButton() {
  const [isReady, setIsReady] = useState(false)
  const { handleSendGTMEvent } = useGTM()
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirectUrl = searchParams.get('redirect_url')

  const handleLoginWithGoogle = useCallback(
    async (response: { credential: string }) => {
      const { credential } = response
      const decodeCredential: { sub: string; email?: string } =
        jwtDecode(credential)

      const resultAction = await loginGoogleAction({
        credential,
      })

      const { success } = resultAction.data || { success: false }

      if (!success) {
        console.error('Login com Google falhou')
        toast.error('Não foi possível fazer login com o Google.')
        return
      }

      const google_id = decodeCredential.sub

      const eventData = {
        environment: 'Vitrine',
        event: 'vitrine',
        eventName: 'lead_social',
        customDataObject: {
          google_id,
        },
      }

      sendGTMEvent({
        event: 'vitrine',
        eventName: 'login',
        environment: 'Vitrine',
        method: 'google',
      })

      trackEvent(eventData)

      toast.success('Login com Google realizado com sucesso!')

      if (redirectUrl) {
        router.push(redirectUrl)
        return
      }

      router.push('/app')
    },
    [router, handleSendGTMEvent, redirectUrl]
  )

  useEffect(() => {
    function loadGsi() {
      const signInDiv = document.getElementById('signInDiv')

      if (
        signInDiv &&
        window.google &&
        window.google.accounts &&
        window.google.accounts.id
      ) {
        window.google.accounts.id.initialize({
          client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '',
          callback: handleLoginWithGoogle,
          auto_select: false,
          ux_mode: 'popup',
          login_hint: '',
        })
        window.google.accounts.id.renderButton(signInDiv, {
          theme: 'outline',
          size: 'large',
          locale: 'pt-br',
          type: 'standard',
          logo_alignment: 'center',
          shape: 'pill',
          width: 300,
        })

        setIsReady(true)
      }
    }

    loadGsi()
  }, [handleLoginWithGoogle])

  return (
    <div className="relative mt-2 flex h-10 w-full max-w-[300px] items-center justify-center rounded-full focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2">
      {/*
        Real "Sign in with Google" button/iframe from GSI. It stays invisible
        because Google personalizes it on its own (generic label ->
        "Continuar como X") once it detects an active session, causing a
        visible flicker. It's kept clickable/focusable so the actual login
        flow (credential callback below) still works — only its appearance
        is hidden behind the static button below.
      */}
      <div className="absolute inset-0 opacity-0" id="signInDiv" />

      {isReady && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-6 text-sm font-medium text-gray-700 shadow-sm"
        >
          <GoogleGlyph />
          <span>Fazer login com o Google</span>
        </div>
      )}

      {!isReady && (
        <Skeleton className="flex h-10 w-full items-center gap-2 rounded-full bg-[#dbe4ff] px-16 py-1">
          <Skeleton className="rounded-full bg-gray-400 p-4" />
          <Skeleton className="h-4 w-full bg-gray-400" />
        </Skeleton>
      )}
    </div>
  )
}

function GoogleGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4818h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.874 2.6836-6.6154z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1805l-2.9087-2.2581c-.8059.54-1.8368.8591-3.0477.8591-2.3436 0-4.3282-1.5831-5.0359-3.7104H.9573v2.3318C2.4382 15.9832 5.4818 18 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.9641 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2822-1.71V4.9582H.9573C.3477 6.1732 0 7.5477 0 9s.3477 2.8268.9573 4.0418L3.9641 10.71z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.5786c1.3214 0 2.5077.4541 3.4405 1.3459l2.5818-2.5818C13.4632.9014 11.4259 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.9641 7.29C4.6718 5.1627 6.6564 3.5786 9 3.5786z"
        fill="#EA4335"
      />
    </svg>
  )
}
